import React, { useEffect, useState } from 'react';
import { Loader, Dimmer, Table } from 'semantic-ui-react';
import API from '../../utils/network';
import { isNullOrUndefined, generatePathForceParams, stringIsNullOrUndefinedOrBlank, divideArray } from '../../utils/siteFunctions';
import { CustomField, Account } from '../../models/GeneralModels';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';

const AccountDetailsPage = ({ acctNumber }: AccountDetailsPageProps) => {
	/// Global State
	const client = useSelector(state => state.ui.client);

	/// Local State
	const [ account, setAccount ] = useState<Account | null>(null);
	const [ fields, setFields ] = useState<CustomField[]>([]);

	/// Effects
	useEffect(() => {
		API.get(`client-portal/account/${encodeURIComponent(acctNumber)}`)
			.then(data => {
				setAccount({
					id: data.data.id,
					accountNumber: data.data.accountNumber,
					clientClaimNumber: data.data.clientClaimNumber,
					enteredDate: new Date(data.data.enteredDate),
					dateOfFirstDelinquency: isNullOrUndefined(data.data.dateOfFirstDelinquency) ? null : new Date(data.data.dateOfFirstDelinquency),
					originatedDate: isNullOrUndefined(data.data.originatedDate) ? null : new Date(data.data.originatedDate),
					debtorPaymentID: data.data.debtorPaymentId,
					collectionStatusDescription: data.data.collectionStatusDescription,
					collectionStatusType: data.data.collectionStatusType,
					currentBalance: data.data.currentBalance,
					totalPayments: data.data.totalPayments,
					totalCosts: data.data.totalCosts,
					totalInterest: data.data.totalInterest,
					debtors: data.data.debtors.map(x => ({
						firstName: x.firstName,
						lastName: x.lastName,
						companyName: x.companyName,
						isCompany: x.isCompany === 'true',
						employerName: x.employerName,
						addresses: x.debtorAddresses?.map(y => ({
							address: y.address,
							addressTwo: y.addressTwo,
							city: y.city,
							state: y.state,
							zip: y.zip,
							isPrimary: y.isPrimary
						})),
						// phoneNumbers: [ { phoneNumber: '555-555-5555' } ],
						phoneNumbers: x.debtorPhones?.map(y => ({
							phoneNumber: y.phone,
							isPrimary: true
						})),
						// emailAddresses: [ { emailAddress: 'test@test.com' } ]
						emailAddresses: x.debtorEmailAddresses?.map(y => ({
							emailAddress: y.email,
							isPrimary: y.isPrimary
						}))
					})),
					customFields: data.data.accountCustomFields?.map(x => ({
						name: x.name,
						datatype: x.dataType,
						value: x.value
					})),
					notes: data.data.notes?.map(x => ({
						date: x.date,
						noteText: x.noteText
					}))
				});
			})
			.catch(error => console.error('Error', error));
	}, [acctNumber]);

	useEffect(() => {
		let fields = [
			{ name: 'Account Number', datatype: 'STRING', value: account?.accountNumber || '' },
			{ name: 'Client Claim Number', datatype: 'STRING', value: account?.clientClaimNumber || '' },
			{ name: 'Current Status', datatype: 'STRING', value: account?.collectionStatusDescription || '' }
		];

		if (client.showFieldsSettings.OriginatedDate) {
			fields = [
				...fields,
				{ name: 'Originated Date', datatype: 'STRING', value: isNullOrUndefined(account?.originatedDate) ? '' : dayjs(account?.originatedDate as Date).format('M/D/YYYY') }
			];
		}

		if (client.showFieldsSettings.DateOfFirstDelinquency) {
			fields = [
				...fields,
				{ name: 'Date of First Delinquency', datatype: 'STRING', value: isNullOrUndefined(account?.dateOfFirstDelinquency) ? '' : dayjs(account?.dateOfFirstDelinquency as Date).format('M/D/YYYY') }
			];
		}

		if (client.showFieldsSettings.DebtorEmployer) {
			fields = [
				...fields,
				{ name: 'Employer', datatype: 'STRING', value: account?.debtors?.[0]?.employerName || '' }
			];
		}

		fields = [
			...fields,
			{ name: 'Current Balance', datatype: 'STRING', value: account?.currentBalance?.toFixed(2) || '' },
			{ name: 'Total Payments', datatype: 'STRING', value: account?.totalPayments?.toFixed(2) || '' }
		];

		if (client.showFieldsSettings.TotalInterest) {
			fields = [
				...fields,
				{ name: 'Total Interest', datatype: 'STRING', value: account?.totalInterest?.toFixed(2) || ''}
			];
		}

		if (client.showFieldsSettings.TotalCosts) {
			fields = [
				...fields,
				{ name: 'Total Costs', datatype: 'STRING', value: account?.totalCosts?.toFixed(2) || '' }
			];
		}

		setFields([
			...fields,
			...(account?.customFields || [])
		]);
	}, [account, client.showFieldsSettings]);

	/// Render
	return isNullOrUndefined(account) ? (
		<div style={{height: '10em'}}>
			<Dimmer active inverted style={{ zIndex: 'unset' }}>
				<Loader size='massive'>Loading</Loader>
			</Dimmer>
		</div>
	) : (
		<>
			<div>
				<h1>{(account?.debtors?.[0].isCompany || false) ? (account?.debtors?.[0].companyName || '') : ((account?.debtors?.[0].firstName || '') + ' ' + (account?.debtors?.[0].lastName || ''))}</h1>
			</div>
			<div style={{
				display: 'grid',
				gridTemplateColumns: '1fr 1fr 1fr',
				alignItems: 'start'
			}}>
				<div>
					<div>{account?.debtors?.[0].addresses?.[0]?.address}</div>
					<div>{account?.debtors?.[0].addresses?.[0]?.city}{account?.debtors?.[0].addresses?.[0]?.city && account?.debtors?.[0].addresses?.[0]?.state && ', '} {account?.debtors?.[0].addresses?.[0]?.state} {account?.debtors?.[0].addresses?.[0]?.zip}</div>
					{client.showFieldsSettings.DebtorPhone && <div>{account?.debtors?.[0].phoneNumbers?.[0]?.phoneNumber?.phoneNumber()}</div>}
					{client.showFieldsSettings.DebtorEmail && <div>{account?.debtors?.[0].emailAddresses?.[0]?.emailAddress}</div>}
				</div>
				<div>
					<div><b>Entered Date:</b> {account?.enteredDate?.getMonth()}/{account?.enteredDate?.getDate()}/{account?.enteredDate?.getFullYear()}</div>
					<div><b>Client Payment ID:</b> {account?.debtorPaymentID}</div>
				</div>
			</div>
			<hr />
			<table><tbody>
				{divideArray(fields, 4)?.map((x, index) => (
					<tr key={index}>
						{ x.map((y, index2) =>
							<td key={index2} style={{ whiteSpace: 'nowrap' }}>
								<b>{y.name}:</b> {formatCustomFieldValue(y)}
							</td>
						)}
					</tr>
				))}
			</tbody></table>
			{!isNullOrUndefined(account?.notes?.length) && (account?.notes?.length as number) > 0 && (
				<>
					<hr />
					<Table compact="very">
						<Table.Header>
							<Table.Row>
								<Table.HeaderCell>Date</Table.HeaderCell>
								<Table.HeaderCell>Note</Table.HeaderCell>
							</Table.Row>
						</Table.Header>
						<Table.Body>
							{account?.notes?.map((x, index) => (
								<Table.Row key={index}>
									<Table.Cell collapsing>{dayjs(x.date).format('M/D/YYYY')}</Table.Cell>
									<Table.Cell><pre>{x.noteText}</pre></Table.Cell>
								</Table.Row>
							))}
						</Table.Body>
					</Table>
				</>
			)}
		</>
	);
};

interface CustomFieldTableCellProps {
	customField: CustomField;
}

const formatCustomFieldValue = (customField:CustomField) => {
	if (customField.value === undefined || customField.value === null)
		return '(Empty)';

	switch (customField.datatype) {
	case 'DATETIME': {
		const dateValue = new Date(customField.value);
		return `${dateValue.getMonth() + 1}/${dateValue.getDate()}/${dateValue.getFullYear()}`;
	}

	case 'BOOL':
		return customField.value as unknown as boolean;

	case 'DECIMAL':
		return (customField.value as unknown as number)?.toFixed(2);

	case 'STRING':
	case 'TEXT':
	case 'DDL':
	case 'INT':
	case 'HYPERLINK':
	default:
		return customField.value;
	}
};

interface AccountDetailsPageProps {
	acctNumber: string
}

export default AccountDetailsPage;